import { ReactElement, type ElementType } from 'react'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import type { InputBaseComponentProps } from '@mui/material'
import { IconButton, InputAdornment, Stack, TextField } from '@mui/material'
import { NumberInput as UiNumberInput } from '@ui/forms'

interface Props {
  label: string
  value: number
  /** Increment/decrement applied by the stepper buttons. */
  step: number
  dataCy: string
  onChange: (value: number) => void
}

/**
 * PDF-cm offset field for the storey image settings. Uses `UiNumberInput` (react-number-format,
 * rendered as `type="text"`) for German decimal formatting, which means the native number spinner
 * is unavailable — so we add explicit up/down stepper buttons to match the sibling `type="number"`
 * fields.
 */
const StoreyImageOffsetField = ({ label, value, step, dataCy, onChange }: Props): ReactElement => {
  // Round to the step's precision so repeated stepping doesn't accumulate float error (e.g. 0.30000004).
  const stepBy = (delta: number) => {
    const decimals = (step.toString().split('.')[1] ?? '').length
    const factor = 10 ** decimals
    onChange(Math.round((value + delta) * factor) / factor)
  }

  return (
    <TextField
      label={label}
      type="text"
      size="small"
      value={value}
      onChange={e => {
        // When using `UiNumberInput` as MUI `inputComponent`, MUI passes a non-DOM event shape.
        // We must not call event methods like `stopPropagation()` here.
        const eventValue = (e as unknown as { target?: { value?: string } }).target?.value ?? ''
        if (!eventValue.length) return
        const pdfCm = Number(eventValue)
        if (!Number.isFinite(pdfCm)) return
        onChange(pdfCm)
      }}
      InputProps={{
        inputComponent: UiNumberInput as unknown as ElementType<InputBaseComponentProps>,
        endAdornment: (
          // A thin stepper strip near the input's right edge, vertically centered and shorter than
          // the field, split into two flat rectangular halves.
          <InputAdornment
            position="end"
            className="storey-offset-stepper"
            sx={{ ml: 0, mr: -0.25 }}
          >
            <Stack direction="column" sx={{ height: 24, backgroundColor: 'grey.300' }}>
              {(
                [
                  // Align the arrows toward the shared center line (up-arrow to the bottom of the
                  // top half, down-arrow to the top of the bottom half) so they cluster near the
                  // middle like the native webkit spinner, rather than at the strip's extremes.
                  { icon: ArrowDropUp, delta: step, align: 'flex-end', aria: `${label} erhöhen` },
                  {
                    icon: ArrowDropDown,
                    delta: -step,
                    align: 'flex-start',
                    aria: `${label} verringern`,
                  },
                ] as const
              ).map(({ icon: Icon, delta, align, aria }) => (
                <IconButton
                  key={aria}
                  size="small"
                  disableRipple
                  onClick={e => {
                    e.stopPropagation()
                    stepBy(delta)
                  }}
                  sx={{
                    padding: 0,
                    flex: 1,
                    minHeight: 0,
                    width: 15,
                    alignItems: align,
                    borderRadius: 0,
                    color: 'text.secondary',
                    '&:hover': { backgroundColor: 'grey.400' },
                  }}
                  aria-label={aria}
                >
                  <Icon sx={{ fontSize: 14 }} />
                </IconButton>
              ))}
            </Stack>
          </InputAdornment>
        ),
      }}
      inputProps={{
        step,
        'data-cy': dataCy,
      }}
      onClick={e => {
        e.stopPropagation()
        e.preventDefault()
      }}
      sx={{
        width: 110,
        // Match the native `type="number"` spinner, which only reveals its arrows while the field
        // is hovered or focused. `visibility` (not display) keeps the layout stable when hidden.
        '& .storey-offset-stepper': { visibility: 'hidden' },
        '&:hover .storey-offset-stepper, &:focus-within .storey-offset-stepper': {
          visibility: 'visible',
        },
      }}
    />
  )
}

export default StoreyImageOffsetField
